import React from 'react';
import { motion } from 'framer-motion';
import { CatLogo } from '@/components/CatLogo';

{/* ========================
 * GlobalOverlay - Theme-aware loading and error overlay component
 * Fully integrated with custom theme system and preferred animation patterns
 * ======================== */}
export const GlobalOverlay = ({ message = 'Loading...', progress = 0, error, onRetry }) => {
  const overlayVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1, // Ensure full opacity when animated
      transition: { duration: 0.3 }
    }
  };

  const contentVariants = {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: 0.1
      }
    }
  };

  const progressVariants = {
    initial: { width: 0 },
    animate: {
      width: `${progress}%`,
      transition: {
        duration: 0.8,
        ease: [0.4, 0.0, 0.2, 1],
        delay: 0.2
      }
    }
  };

  return (
    <motion.div
      variants={overlayVariants}
      initial="initial"
      animate="animate"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center backdrop-blur-sm"
      style={{
        // Use the background-dark color from the default theme directly as a fallback
        // This ensures the overlay has a dark background even before theme variables are fully parsed
        backgroundColor: 'rgb(5, 6, 7)', // Matches Theme 1 --background-dark-rgb
        opacity: 1 // Ensure the overlay is fully opaque when visible
      }}
    >
      {!error ? (
        <motion.div
          variants={contentVariants}
          className="flex flex-col items-center space-y-6"
        >
          {/* ========================
           * Loading State - Cat logo with glow animation
           * ======================== */}
          <div className="animate-glow">
            <CatLogo
              size={32}
              className="text-primary transition-colors duration-300"
            />
          </div>

          <div className="flex flex-col items-center space-y-4">
            {/* ========================
             * Loading Message - Theme-aware text styling
             * ======================== */}
            <motion.span
              className="text-sm font-medium transition-colors duration-300"
              style={{ color: 'var(--on-surface-dim)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              {message}
            </motion.span>

            {/* ========================
             * Progress Bar - Theme-aware with smooth animations
             * ======================== */}
            <div className="w-48 relative">
              <div
                className="h-0.5 rounded-full overflow-hidden transition-colors duration-300"
                style={{ backgroundColor: 'var(--surface-raised)' }}
              >
                <motion.div
                  className="h-full rounded-full animate-glow transition-colors duration-300"
                  style={{ backgroundColor: 'var(--primary)' }}
                  variants={progressVariants}
                />
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          variants={contentVariants}
          className="flex flex-col items-center space-y-6 p-8 rounded-xl transition-all duration-300"
          style={{
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border-color)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
          }}
        >
          {/* ========================
           * Error State - Cat logo with error styling
           * ======================== */}
          <CatLogo
            size={32}
            className="transition-colors duration-300"
            style={{ color: 'var(--error)' }}
          />

          {/* ========================
           * Error Content - Theme-aware typography
           * ======================== */}
          <div className="flex flex-col items-center space-y-3 text-center">
            <h2
              className="text-lg font-semibold transition-colors duration-300"
              style={{ color: 'var(--error)' }}
            >
              Initialization Error
            </h2>
            <p
              className="text-sm max-w-xs transition-colors duration-300"
              style={{ color: 'var(--on-surface-dim)' }}
            >
              {error.message}
            </p>
          </div>

          {/* ========================
           * Retry Button - Theme-aware with hover states
           * ======================== */}
          <button
            onClick={onRetry}
            className="px-6 py-2.5 rounded-lg font-medium transition-all duration-200 hover:scale-105 active:scale-95 animate-glow"
            style={{
              backgroundColor: 'var(--primary)',
              color: 'var(--primary-light)',
              border: '1px solid var(--primary-dark)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--primary-dark)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--primary)';
            }}
          >
            Retry
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

GlobalOverlay.displayName = 'GlobalOverlay';